/** Shared live/replay delivery for the markup outbox. Network work is outside
 * IndexedDB transactions; only claims and exact acknowledgements are transactional. */
import { isAxiosError } from "axios";
import {
    MarkupApi,
    MarkupVersionConflict,
    type ApiMarkupSaveResult,
    type MarkupScopeMetadata,
} from "../../../api/markup";
import {
    acknowledgeMarkupOutboxOperation,
    claimNextMarkupOutboxOperation,
    enqueueMarkupOutboxOperation,
    markMarkupOutboxOperation,
    isMarkupOutboxDeliveredThrough,
    rebaseClaimedMarkupOutboxOperation,
    recordMarkupOutboxScopeMetadata,
    hasMarkupOutboxEverFenced,
    readMarkupOutboxPendingOperations,
    type MarkupOutboxOperation,
    type MarkupOutboxPayload,
    type MarkupOutboxScope,
    type SaveOperationPayload,
} from "./markupOutbox";
import { mergeMarkupCalibration, mergeMarkupIntent } from "./markupConflict";

const RETRY_BASE_MS = 4_000;
const RETRY_MAX_MS = 5 * 60_000;
const MAX_CONFLICT_RETRIES = 3;

function retryAt(attempts: number): number {
    return Date.now() + Math.min(RETRY_BASE_MS * 2 ** Math.max(0, attempts), RETRY_MAX_MS);
}

function retryable(error: unknown): boolean {
    return (
        isAxiosError(error) &&
        (error.response === undefined ||
            error.response.status === 408 ||
            error.response.status === 429 ||
            error.response.status >= 500)
    );
}

/**
 * The legacy (unfenced) Clear: delete this scope's draft, then purge clear history, returning
 * "stale" as soon as `isScopeCurrent` fails between requests (none is checked after the purge).
 * A file whose markup is a layer of its own clears in one request, fenced by the version this tab
 * last saw.
 */
async function legacyClearScope(
    scope: Pick<MarkupOutboxScope, "fileId" | "page" | "revisionId" | "sectionGroupId">,
    isScopeCurrent: () => boolean,
): Promise<"cleared" | "stale"> {
    if (await MarkupApi.clearLayer(scope.fileId)) return "cleared";
    const sets = await MarkupApi.getVisible(
        scope.fileId,
        scope.page,
        scope.revisionId,
        scope.sectionGroupId,
    );
    if (!isScopeCurrent()) return "stale";
    const draft = sets.find((set) => set.status === "draft" && set.is_own);
    if (draft) await MarkupApi.remove(draft.id);
    if (!isScopeCurrent()) return "stale";
    await MarkupApi.purgeHistory(scope.fileId, scope.page, scope.revisionId, scope.sectionGroupId);
    return "cleared";
}

export type MarkupOutboxDeliveryResult =
    | { status: "idle" | "delivered"; saved?: ApiMarkupSaveResult }
    | { status: "retrying"; error?: unknown }
    | { status: "conflict" | "failed" | "unsupported-clear"; error?: unknown };

/** Delivers no more than the contiguous head for a scope. Call again on a
 * connectivity observation or after enqueue; a later operation cannot bypass it. */
export async function deliverNextMarkupOutboxOperation(
    scope: MarkupOutboxScope,
    owner: string,
    isScopeCurrent: () => boolean = () => true,
    foregroundToken?: string,
    legacyClearOperationId?: string,
): Promise<MarkupOutboxDeliveryResult> {
    let operation: MarkupOutboxOperation | null = null;
    const fail = async (error: unknown): Promise<MarkupOutboxDeliveryResult> => {
        if (!operation)
            return retryable(error) ? { status: "retrying", error } : { status: "failed", error };
        if (!isScopeCurrent()) {
            await markMarkupOutboxOperation(operation, owner, "pending");
            return { status: "idle" };
        }
        const status =
            error instanceof MarkupVersionConflict
                ? "conflict"
                : retryable(error)
                  ? "pending"
                  : "failed";
        await markMarkupOutboxOperation(
            operation,
            owner,
            status,
            status === "pending" ? retryAt(operation.attempts) : Date.now(),
        );
        return { status: status === "pending" ? "retrying" : status, error };
    };
    try {
        if (!isScopeCurrent() || (await readMarkupOutboxPendingOperations(scope)).length === 0)
            return { status: "idle" };
        if (!isScopeCurrent()) return { status: "idle" };
        let metadata: MarkupScopeMetadata;
        try {
            metadata = await MarkupApi.getScopeMetadata(
                scope.fileId,
                scope.page,
                scope.revisionId,
                scope.sectionGroupId,
            );
        } catch (metadataError) {
            // An old server has no metadata route. Preserve ordinary save/online Clear
            // behavior; only a server that has ever fenced this scope may require it.
            if (!isAxiosError(metadataError) || metadataError.response?.status !== 404)
                throw metadataError;
            if (await hasMarkupOutboxEverFenced(scope)) {
                throw new Error("Fenced markup scope cannot downgrade to legacy delivery");
            }
            metadata = { capability_version: 0, fenced_mode: false, scope_generation: 0 };
        }
        if (!isScopeCurrent()) return { status: "idle" };
        // Fenced writes need the complete server contract. Without a canonical ID
        // we cannot persist its generation/capability latch before sending a write.
        if (
            metadata.fenced_mode &&
            (metadata.capability_version < 1 ||
                !Number.isSafeInteger(metadata.scope_generation) ||
                metadata.scope_generation < 0 ||
                typeof metadata.scope_id !== "string" ||
                metadata.scope_id.trim().length === 0)
        )
            throw new Error("Markup server returned incomplete fenced scope metadata");
        // A feature flag rollback cannot re-enable unfenced delivery for a scope
        // whose existing operations already rely on durable server receipts.
        if (!metadata.fenced_mode && (await hasMarkupOutboxEverFenced(scope))) {
            throw new Error("Fenced markup scope cannot downgrade to legacy delivery");
        }
        if (metadata.scope_id) {
            const metadataState = await recordMarkupOutboxScopeMetadata(scope, {
                canonicalScopeId: metadata.scope_id,
                generation: metadata.scope_generation,
                fenced: metadata.fenced_mode,
            });
            if (metadataState === "stale") {
                throw new Error("Markup canonical scope changed");
            }
        }
        // Metadata seeding must complete before claiming: it may update an unknown
        // offline generation, but never a row that another renderer already holds.
        operation = await claimNextMarkupOutboxOperation(scope, owner, undefined, foregroundToken);
        if (!operation) return { status: "idle" };
        if (!isScopeCurrent()) {
            await markMarkupOutboxOperation(operation, owner, "pending");
            return { status: "idle" };
        }
        let saved: ApiMarkupSaveResult | undefined;
        if (operation.payload.kind === "clear") {
            if (!metadata.fenced_mode) {
                // The legacy multi-request Clear is allowed only by this foreground
                // click. A reconnect/restart must not replay it against newer work.
                if (legacyClearOperationId !== operation.operationId) {
                    await markMarkupOutboxOperation(operation, owner, "failed");
                    return {
                        status: "unsupported-clear",
                        error: new Error(
                            "Clear needs an online retry or a server with durable Clear support",
                        ),
                    };
                }
                if ((await legacyClearScope(scope, isScopeCurrent)) === "stale") {
                    await markMarkupOutboxOperation(operation, owner, "pending");
                    return { status: "idle" };
                }
                if (!isScopeCurrent()) {
                    await markMarkupOutboxOperation(operation, owner, "pending");
                    return { status: "idle" };
                }
                if (!(await acknowledgeMarkupOutboxOperation(operation, owner))) {
                    return {
                        status: "failed",
                        error: new Error("Markup Clear acknowledgement lease expired"),
                    };
                }
                return { status: "delivered" };
            }
            if (metadata.capability_version < 1 || !metadata.fenced_mode) {
                await markMarkupOutboxOperation(operation, owner, "failed");
                return { status: "unsupported-clear" };
            }
            const receipt = await MarkupApi.clearScope(
                scope.fileId,
                scope.page,
                scope.revisionId,
                {
                    operation_id: operation.operationId,
                    scope_generation: operation.generation,
                },
                scope.sectionGroupId,
            );
            if (
                receipt.operation_id !== operation.operationId ||
                receipt.scope_generation !== operation.generation + 1 ||
                receipt.scope_id !== metadata.scope_id
            ) {
                await markMarkupOutboxOperation(operation, owner, "failed");
                return {
                    status: "failed",
                    error: new Error("Markup server did not confirm this Clear"),
                };
            }
        } else {
            const saveResult = await MarkupApi.saveDraft(
                scope.fileId,
                scope.page,
                scope.revisionId,
                operation.payload.shapes,
                operation.payload.calibration,
                operation.payload.expectedVersion,
                metadata.fenced_mode
                    ? {
                          operation_id: operation.operationId,
                          scope_generation: operation.generation,
                      }
                    : undefined,
                scope.sectionGroupId,
            );
            // A delivery-mode server must receipt the exact id. A legacy success cannot
            // prove a lost-response retry was safe, so preserve this row for recovery.
            if (
                metadata.fenced_mode &&
                (saveResult.delivery?.operation_id !== operation.operationId ||
                    saveResult.delivery.scope_generation !== operation.generation ||
                    saveResult.delivery.scope_id !== metadata.scope_id ||
                    saveResult.delivery.confirmed_version !== saveResult.version ||
                    !saveResult.delivery.payload_sha256)
            ) {
                await markMarkupOutboxOperation(operation, owner, "failed");
                return {
                    status: "failed",
                    error: new Error("Markup server did not confirm this operation"),
                };
            }
            saved = saveResult;
        }
        if (!isScopeCurrent()) {
            await markMarkupOutboxOperation(operation, owner, "pending");
            return { status: "idle" };
        }
        if (!(await acknowledgeMarkupOutboxOperation(operation, owner))) {
            return { status: "failed", error: new Error("Markup acknowledgement lease expired") };
        }
        return { status: "delivered", saved };
    } catch (error) {
        if (!operation && isScopeCurrent()) {
            // Record negotiation failures/backoff on an owned row as well. Another
            // window's active claim is never changed merely because metadata failed.
            operation = await claimNextMarkupOutboxOperation(
                scope,
                owner,
                undefined,
                foregroundToken,
            );
        }
        if (!operation) return fail(error);
        if (error instanceof MarkupVersionConflict) {
            if (operation.payload.kind === "save" && operation.attempts < MAX_CONFLICT_RETRIES) {
                try {
                    const sets = await MarkupApi.getVisible(
                        scope.fileId,
                        scope.page,
                        scope.revisionId,
                        scope.sectionGroupId,
                    );
                    if (!isScopeCurrent()) {
                        await markMarkupOutboxOperation(operation, owner, "pending");
                        return { status: "idle" };
                    }
                    const remote = sets.find((set) => set.status === "draft" && set.is_own);
                    const merge = mergeMarkupIntent(
                        operation.payload.baseline?.shapes ?? null,
                        operation.payload.shapes,
                        remote?.shapes ?? [],
                    );
                    const calibration = mergeMarkupCalibration(
                        operation.payload.baseline,
                        operation.payload.calibration,
                        remote?.calibration ?? null,
                    );
                    if (!merge.conflict && !calibration.conflict) {
                        // Rewrite the claimed head atomically; never delete/re-enqueue it,
                        // because a crash could otherwise lose intent or cross Clear.
                        if (
                            !(await rebaseClaimedMarkupOutboxOperation(operation, owner, {
                                kind: "save",
                                shapes: merge.shapes,
                                calibration: calibration.calibration,
                                expectedVersion: remote?.version ?? null,
                                baseline: {
                                    shapes: remote?.shapes ?? [],
                                    calibration: remote?.calibration ?? null,
                                },
                            }))
                        ) {
                            return {
                                status: "failed",
                                error: new Error("Markup rebase lease expired"),
                            };
                        }
                        return deliverNextMarkupOutboxOperation(
                            scope,
                            owner,
                            isScopeCurrent,
                            foregroundToken,
                            legacyClearOperationId,
                        );
                    }
                } catch (rebaseError) {
                    return fail(rebaseError);
                }
            }
            // Missing baseline or same-shape concurrency remains visible and blocks the scope.
            await markMarkupOutboxOperation(operation, owner, "conflict");
            return { status: "conflict", error };
        }
        return fail(error);
    }
}

/** Foreground lifecycle barrier used by Publish. It never treats a local commit as
 * delivery, and does not consume later edits beyond the captured sequence. */
export async function deliverMarkupOutboxThrough(
    scope: MarkupOutboxScope,
    owner: string,
    sequence: number,
    isScopeCurrent: () => boolean = () => true,
    foregroundToken?: string,
    legacyClearOperationId?: string,
): Promise<MarkupOutboxDeliveryResult> {
    let saved: ApiMarkupSaveResult | undefined;
    while (!(await isMarkupOutboxDeliveredThrough(scope, sequence))) {
        const result = await deliverNextMarkupOutboxOperation(
            scope,
            owner,
            isScopeCurrent,
            foregroundToken,
            legacyClearOperationId,
        );
        if (result.status !== "delivered") return result;
        if (result.saved) saved = result.saved;
    }
    return { status: "delivered", saved };
}

export function markupOutboxWindowOwner(): string {
    return globalThis.crypto?.randomUUID?.() ?? `markup-window-${Date.now()}-${Math.random()}`;
}

/**
 * One direct attempt for a host without a durable outbox (web): a conflict gets one rebase and
 * retry with the durable path's merge helpers. It refuses a scope a durable client has fenced.
 */
async function deliverMarkupOperationDirectly(
    scope: Pick<MarkupOutboxScope, "fileId" | "page" | "revisionId" | "sectionGroupId">,
    payload: MarkupOutboxPayload,
    isScopeCurrent: () => boolean,
): Promise<MarkupOutboxDeliveryResult> {
    const saveDraft = (
        shapes: SaveOperationPayload["shapes"],
        calibration: SaveOperationPayload["calibration"],
        expectedVersion: SaveOperationPayload["expectedVersion"],
    ) =>
        MarkupApi.saveDraft(
            scope.fileId,
            scope.page,
            scope.revisionId,
            shapes,
            calibration,
            expectedVersion,
            undefined,
            scope.sectionGroupId,
        );
    try {
        let metadata: MarkupScopeMetadata;
        try {
            metadata = await MarkupApi.getScopeMetadata(
                scope.fileId,
                scope.page,
                scope.revisionId,
                scope.sectionGroupId,
            );
        } catch (metadataError) {
            // A server without the metadata route has fenced nothing: the legacy write goes.
            if (!isAxiosError(metadataError) || metadataError.response?.status !== 404)
                throw metadataError;
            metadata = { capability_version: 0, fenced_mode: false, scope_generation: 0 };
        }
        if (!isScopeCurrent()) return { status: "idle" };
        if (metadata.fenced_mode) {
            return {
                status: "failed",
                error: new Error("Fenced markup scope requires the durable outbox to write"),
            };
        }
        if (payload.kind === "clear") {
            try {
                const result = await legacyClearScope(scope, isScopeCurrent);
                return { status: result === "stale" ? "idle" : "delivered" };
            } catch (error) {
                // The markups changed since this tab read them: nothing was cleared.
                if (error instanceof MarkupVersionConflict) return { status: "conflict", error };
                throw error;
            }
        }
        try {
            const saved = await saveDraft(
                payload.shapes,
                payload.calibration,
                payload.expectedVersion,
            );
            return { status: "delivered", saved };
        } catch (error) {
            if (!(error instanceof MarkupVersionConflict)) throw error;
            if (!isScopeCurrent()) return { status: "idle" };
            const sets = await MarkupApi.getVisible(
                scope.fileId,
                scope.page,
                scope.revisionId,
                scope.sectionGroupId,
            );
            if (!isScopeCurrent()) return { status: "idle" };
            const remote = sets.find((set) => set.status === "draft" && set.is_own);
            const merge = mergeMarkupIntent(
                payload.baseline?.shapes ?? null,
                payload.shapes,
                remote?.shapes ?? [],
            );
            const calibration = mergeMarkupCalibration(
                payload.baseline,
                payload.calibration,
                remote?.calibration ?? null,
            );
            if (merge.conflict || calibration.conflict) return { status: "conflict", error };
            if (!isScopeCurrent()) return { status: "idle" };
            try {
                const saved = await saveDraft(
                    merge.shapes,
                    calibration.calibration,
                    remote?.version ?? null,
                );
                return { status: "delivered", saved };
            } catch (retryError) {
                // One retry only; a second conflict stays visible for recovery.
                if (retryError instanceof MarkupVersionConflict) {
                    return { status: "conflict", error: retryError };
                }
                throw retryError;
            }
        }
    } catch (error) {
        return { status: "failed", error };
    }
}

export type MarkupOperationSubmission = MarkupOutboxDeliveryResult & {
    /** The durable queue's record (desktop), for retry recovery; absent on the web path. */
    operation?: MarkupOutboxOperation;
};

/** The one way a save, publish flush or Clear submits: through the IndexedDB outbox (desktop) or
 *  directly to the server (web). */
export async function submitMarkupOperation(
    hasDurableOutbox: boolean,
    scope: MarkupOutboxScope,
    payload: MarkupOutboxPayload,
    owner: string,
    isScopeCurrent: () => boolean = () => true,
    foregroundToken?: string,
): Promise<MarkupOperationSubmission> {
    if (!hasDurableOutbox) return deliverMarkupOperationDirectly(scope, payload, isScopeCurrent);
    const operation = await enqueueMarkupOutboxOperation(scope, payload);
    const delivery = await deliverMarkupOutboxThrough(
        scope,
        owner,
        operation.sequence,
        isScopeCurrent,
        foregroundToken,
    );
    return { ...delivery, operation };
}
